import PropTypes from "prop-types";
import { Cell, Pie, PieChart as RechartsPieChart } from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import ChartFilterBadges from "../chart-filter-badges";

// Default theme-aware colour ramp. Callers can still pass
// `colorScale` to override.
const DEFAULT_COLOURS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

const SLICE_STROKE = "var(--background)";

PieChart.propTypes = {
  title: PropTypes.string,
  noDataMessage: PropTypes.string,
  colorScale: PropTypes.array,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      y: PropTypes.number,
    }),
  ),
  style: PropTypes.shape({
    pie: PropTypes.object,
    tooltip: PropTypes.object,
    title: PropTypes.object,
    activeFilters: PropTypes.object,
  }),
  tooltipLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  enableTooltip: PropTypes.bool,
  activeFilters: PropTypes.array,
};

export default function PieChart({
  title,
  noDataMessage,
  colorScale,
  data,
  style,
  tooltipLabel,
  isLoading,
  enableTooltip,
  activeFilters,
}) {
  const dataLength = data?.length ?? 0;
  const total = (data || []).reduce((sum, item) => sum + item.y, 0);
  const colours = colorScale && colorScale.length > 0 ? colorScale : DEFAULT_COLOURS;

  if (isLoading) {
    return (
      <div>
        {title ? (
          <h3 style={style?.title} className="mb-1 ml-1">
            {title}
          </h3>
        ) : null}
        <Skeleton className="h-[350px] w-full rounded-md" />
      </div>
    );
  }

  if (dataLength === 0) {
    return (
      <div className="h-[350px]">
        {title ? (
          <h3 style={style?.title} className="mb-4 ml-1">
            {title}
          </h3>
        ) : null}
        <ChartFilterBadges
          filters={activeFilters}
          style={style?.activeFilters}
        />
        <div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
          {noDataMessage}
        </div>
      </div>
    );
  }

  return (
    <div>
      {title ? (
        <h3 style={style?.title} className="mb-4 ml-1">
          {title}
        </h3>
      ) : null}
      <ChartFilterBadges filters={activeFilters} style={style?.activeFilters} />
      <ChartContainer
        config={{}}
        className="mx-auto aspect-square max-h-[350px]"
      >
        <RechartsPieChart>
          {enableTooltip ? (
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  formatter={(value, _name, item) => {
                    const x = item?.payload?.x;
                    const percentage = ((value / total) * 100).toFixed(2);
                    const rounded = Math.round(value * 100) / 100;
                    if (tooltipLabel) {
                      return tooltipLabel
                        .replace("{x}", x)
                        .replace("{y}", rounded)
                        .replace("{percentage}", percentage);
                    }
                    return `${x}: ${rounded} (${percentage}%)`;
                  }}
                />
              }
            />
          ) : null}
          <Pie
            data={data}
            dataKey="y"
            nameKey="x"
            innerRadius="50%"
            outerRadius="80%"
            stroke={SLICE_STROKE}
            strokeWidth={1}
            isAnimationActive
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colours[index % colours.length]}
              />
            ))}
          </Pie>
        </RechartsPieChart>
      </ChartContainer>
    </div>
  );
}
